import { useLocation, Link } from 'react-router-dom';
import { useLoyalty } from '../../../contexts/LoyaltyContext';

interface LoyaltySidebarNavProps {
  items?: Array<{ label: string; icon: string; href: string }>;
  showUserInfo?: boolean;
}

const DEFAULT_ITEMS = [
  { label: 'Overview', icon: 'layout-dashboard', href: '/' },
  { label: 'Game Arena', icon: 'gamepad-2', href: '/games' },
  { label: 'Missions', icon: 'target', href: '/missions' },
  { label: 'Leaderboard', icon: 'trophy', href: '/rankings' },
  { label: 'Shop Rewards', icon: 'shopping-bag', href: '/rewards' },
];

/* ─── Inline SVG icons ──────────────────────────────────────────────────── */

const ICONS: Record<string, JSX.Element> = {
  'layout-dashboard': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  ),
  'gamepad-2': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="6" y1="11" x2="10" y2="11" />
      <line x1="8" y1="9" x2="8" y2="13" />
      <line x1="15" y1="12" x2="15.01" y2="12" />
      <line x1="18" y1="10" x2="18.01" y2="10" />
      <path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.544-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" />
    </svg>
  ),
  target: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  trophy: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  ),
  'shopping-bag': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  ),
};

function NavIcon({ name }: { name: string }) {
  return ICONS[name] ?? ICONS['layout-dashboard'];
}

function UserInfoCard() {
  const { data, loading } = useLoyalty();

  if (loading) {
    return (
      <div className="p-4 mb-3 animate-pulse">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[var(--color-surface-border)]" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-24 bg-[var(--color-surface-border)] rounded" />
            <div className="h-3 w-16 bg-[var(--color-surface-border)] rounded" />
          </div>
        </div>
        <div className="h-2 w-full bg-[var(--color-surface-border)] rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { profile, tierProgress } = data;
  const initials = `${profile.firstName?.[0] ?? ''}${profile.lastName?.[0] ?? ''}`.toUpperCase() || '?';

  return (
    <div className="p-4 mb-3 border-b border-[var(--color-surface-border,#334155)]">
      {/* Avatar + Name */}
      <div className="flex items-center gap-3 mb-3">
        {profile.avatarUrl ? (
          <img
            src={profile.avatarUrl}
            alt={`${profile.firstName} ${profile.lastName}`}
            className="w-10 h-10 rounded-full object-cover border-2 border-[var(--color-primary)]"
          />
        ) : (
          <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm bg-[var(--color-primary)] text-white">
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="loyalty-heading text-sm text-[var(--color-text)] truncate">
            {profile.firstName} {profile.lastName}
          </div>
          {profile.tierName && (
            <span
              className="loyalty-tag-skew text-[10px] font-bold px-2 py-0.5 rounded-full text-white inline-block mt-0.5"
              style={{ backgroundColor: profile.tierColor ?? 'var(--color-primary)' }}
            >
              {profile.tierName}
            </span>
          )}
        </div>
      </div>

      {/* XP progress bar */}
      {tierProgress.next && (
        <div>
          <div className="flex items-center justify-between text-[10px] text-[var(--color-text-muted)] mb-1">
            <span>{profile.pointsBalance.toLocaleString()} XP</span>
            <span>{tierProgress.next.name}</span>
          </div>
          <div className="h-1.5 rounded-full bg-[var(--color-surface-border,#334155)] overflow-hidden">
            <div
              className="loyalty-progress-bar h-full"
              style={{ width: `${Math.min(tierProgress.progressPercent, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function LoyaltySidebarNavBlock({ props }: { props: LoyaltySidebarNavProps }) {
  const location = useLocation();
  const items = props.items?.length ? props.items : DEFAULT_ITEMS;
  const showUserInfo = props.showUserInfo ?? true;

  return (
    <aside className="hidden md:flex flex-col w-56 flex-shrink-0 sticky top-20 self-start loyalty-card overflow-hidden">
      {showUserInfo && <UserInfoCard />}

      <nav className="flex flex-col py-2">
        {items.map((item) => {
          const isActive =
            item.href === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.href);

          return (
            <Link
              key={item.href}
              to={item.href}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted,#94a3b8)] hover:bg-[var(--color-surface-border,#334155)]/50 hover:text-[var(--color-text)]'
              }`}
            >
              <NavIcon name={item.icon} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
